import { useCallback, useEffect, useRef, useState } from 'react'
import { shareEqual } from '@/ui/share'

// 실시간 변경 알림이 끊겨도 화면을 복구하는 기본 polling 경로다.
// 탭이 숨으면 멈춘다 — 보이지도 않는 화면 때문에 백엔드를 두드리지 않는다.
// refresh 는 변경 알림이 왔을 때 간격을 기다리지 않고 바로 한 번 읽는다.
// 한 번에 하나만 묻는다 — 응답이 간격보다 늦으면 그 틱은 건너뛴다. 겹쳐 물으면 느린 응답 위에
// 요청이 쌓이고, 화면을 떠난 뒤에도 서버가 그 줄을 치른다(#699). refresh 와 탭 복귀는 건너뛰지 않고
// 지금 읽기가 끝난 뒤 한 번 더 읽는다 — 쓰기 직후의 읽기가 쓰기 전의 응답으로 끝나지 않게.
// 받은 값은 이전 값과 구조를 나눈다(`ui/share.ts`) — 같은 부분은 이전 객체라, 응답 전체가 같으면 상태가 그대로이고
// 화면을 다시 그리지 않는다. 그래서 폴링이 다시 그려 주는 것에 기대지 않는다: 시간이 흐르게 하는 글자는 시계가 맡는다
// (`hooks/useNow.ts`).

export type Poll<T> = {
  data: T | null
  error: Error | null
  loading: boolean
  refresh: () => void
}

export function usePoll<T>(fn: () => Promise<T>, intervalMs: number, deps: unknown[] = []): Poll<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [loading, setLoading] = useState(true)

  const fnRef = useRef(fn)
  useEffect(() => {
    fnRef.current = fn
  })
  // 현재 effect 의 run. 언마운트·재시작 사이에는 아무것도 하지 않는다.
  const runRef = useRef<() => void>(() => {})

  useEffect(() => {
    let alive = true
    // 지금 읽는 중인가, 그 사이에 다시 읽으라는 말이 있었는가.
    let busy = false
    let again = false

    const run = async () => {
      if (busy) {
        again = true
        return
      }
      busy = true
      again = false
      try {
        const value = await fnRef.current()
        if (alive) {
          setData((prev) => shareEqual(prev, value))
          setError(null)
        }
      } catch (e) {
        if (alive) setError(e instanceof Error ? e : new Error(String(e)))
      } finally {
        busy = false
        if (alive) setLoading(false)
      }
      if (again && alive) void run()
    }
    runRef.current = () => {
      void run()
    }

    void run()
    let timer = window.setInterval(() => {
      if (document.hidden || busy) return
      void run()
    }, intervalMs)

    // 탭으로 돌아오면 기다리지 않고 바로 한 번 당긴다.
    const onVisible = () => {
      if (!document.hidden) void run()
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      alive = false
      runRef.current = () => {}
      window.clearInterval(timer)
      timer = 0
      document.removeEventListener('visibilitychange', onVisible)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, ...deps])

  const refresh = useCallback(() => runRef.current(), [])
  return { data, error, loading, refresh }
}
